import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-hot-toast';
import { LuPlus, LuX } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import FirmaPad from '../ui/FirmaPad';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';
import { buildSelectStyles } from './ModalCotizacionVolumen';
import { generarActaEntrega } from '../../utils/pdf/propuestaAlquiler';

// Acta de entrega de equipos en alquiler (31-oct-2026): quién recibe (nombre, DNI, cargo) firma en el celu
// y sale el PDF con los N/S de cada equipo. Si la sede ya tiene los equipos cargados, se completan solos.
const INPUT = 'w-full h-11 px-3 rounded-xl outline-none bg-chip border border-black/[0.07] dark:border-white/[0.07] text-ink text-sm font-bold focus:border-brand-red';
const LABEL = 'block text-label font-black text-muted uppercase mb-1.5 tracking-widest';
const filasVacias = (cant, equipo) => Array.from({ length: Math.max(1, cant) }, () => ({ equipo, numeroSerie: '', ubicacion: '' }));

function Campo({ label, children, className = '' }) {
    return <label className={`block ${className}`}><span className={LABEL}>{label}</span>{children}</label>;
}

export default function ModalActaEntrega({ onCerrar, cantidad = 5, equipo = 'Dispenser de agua frío/calor' }) {
    const { isDark } = useTheme();
    const { usuario } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [clienteOpt, setClienteOpt] = useState(null);
    const [sedeId, setSedeId] = useState('');
    const [filas, setFilas] = useState(() => filasVacias(cantidad, equipo));
    const [recibe, setRecibe] = useState({ nombre: '', dni: '', cargo: '' });
    const [firmaRecibe, setFirmaRecibe] = useState(null);
    const [firmaEntrega, setFirmaEntrega] = useState(null);
    const [observaciones, setObservaciones] = useState('');

    useEffect(() => {
        api.get('/clientes?page=0&size=500')
            .then(r => setClientes((r.data.content || r.data).map(c => ({ value: c.id, label: c.nombre, c }))))
            .catch(() => toast.error('No se pudieron cargar los clientes'));
    }, []);

    const cliente = clienteOpt?.c || null;
    const sedes = (cliente?.sedes || []).filter(s => s.direccion);
    const sede = sedes.find(s => String(s.id) === String(sedeId)) || sedes[0] || null;

    // Al elegir la sede: si tiene equipos cargados, la tabla sale de ahí
    useEffect(() => {
        const eqs = sede?.equipos || [];
        if (eqs.length) {
            setFilas(eqs.map(q => ({
                equipo: [q.marca, q.modelo].filter(Boolean).join(' ') || equipo,
                numeroSerie: q.numeroSerie || '', ubicacion: [q.ubicacion, q.piso && `Piso ${q.piso}`, q.sector].filter(Boolean).join(' · '),
            })));
        }
    }, [sede?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const setFila = (i, k) => (ev) => setFilas(p => p.map((f, j) => (j === i ? { ...f, [k]: ev.target.value } : f)));
    const setR = (k) => (ev) => setRecibe(p => ({ ...p, [k]: ev.target.value }));

    const generar = () => {
        if (!cliente) { toast.error('Elegí la empresa'); return; }
        if (!recibe.nombre.trim() || !recibe.dni.trim()) { toast.error('Falta el nombre y el DNI de quien recibe'); return; }
        if (!firmaRecibe) { toast.error('Falta la firma de quien recibe'); return; }
        try {
            generarActaEntrega({
                cliente, sede, autor: usuario?.nombre,
                e: { equipos: filas, recibe, firmaRecibe, firmaEntrega, entrega: usuario?.nombre, observaciones },
            });
        } catch (err) {
            console.error(err);
            toast.error('No se pudo armar el PDF');
        }
    };

    return (
        <ModalShell titulo="Acta de entrega" subtitulo="Equipos en alquiler · firma de quien recibe" onCerrar={onCerrar}
            pie={<button type="button" onClick={generar} className="w-full h-12 rounded-xl font-black text-body text-white bg-brand-red active:scale-95">Descargar acta</button>}>
            <div className="space-y-3">
                <Campo label="Empresa">
                    <Select options={clientes} value={clienteOpt} onChange={(o) => { setClienteOpt(o); setSedeId(''); }} isClearable
                        placeholder="Elegir de Clientes…" noOptionsMessage={() => 'Sin resultados'}
                        styles={buildSelectStyles(isDark)} menuPortalTarget={document.body} />
                </Campo>
                {sedes.length > 1 && (
                    <Campo label="Dirección de instalación">
                        <select className={INPUT} value={sede?.id || ''} onChange={(ev) => setSedeId(ev.target.value)}>
                            {sedes.map(s => <option key={s.id} value={s.id}>{s.nombreSede ? `${s.nombreSede} · ` : ''}{s.direccion}</option>)}
                        </select>
                    </Campo>
                )}

                <div>
                    <span className={LABEL}>Equipos entregados ({filas.length})</span>
                    <div className="space-y-2">
                        {filas.map((f, i) => (
                            <div key={i} className="rounded-xl bg-chip/60 p-2 space-y-1.5">
                                <div className="flex gap-1.5">
                                    <input className={INPUT} value={f.equipo} onChange={setFila(i, 'equipo')} placeholder="Equipo" aria-label={`Equipo ${i + 1}`} />
                                    <button type="button" onClick={() => setFilas(p => p.filter((_, j) => j !== i))} disabled={filas.length === 1}
                                        aria-label={`Quitar equipo ${i + 1}`} className="w-11 h-11 shrink-0 rounded-xl bg-chip text-muted flex items-center justify-center disabled:opacity-30"><LuX size={16} /></button>
                                </div>
                                <div className="grid grid-cols-2 gap-1.5">
                                    <input className={INPUT} value={f.numeroSerie} onChange={setFila(i, 'numeroSerie')} placeholder="N/S" aria-label={`N/S equipo ${i + 1}`} />
                                    <input className={INPUT} value={f.ubicacion} onChange={setFila(i, 'ubicacion')} placeholder="Ubicación" aria-label={`Ubicación equipo ${i + 1}`} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <button type="button" onClick={() => setFilas(p => [...p, { equipo, numeroSerie: '', ubicacion: '' }])}
                        className="mt-2 h-10 px-3 rounded-xl bg-chip text-ink text-sm font-bold inline-flex items-center gap-1.5 active:scale-95"><LuPlus size={15} /> Agregar equipo</button>
                </div>

                <Campo label="Recibe · nombre y apellido"><input className={INPUT} value={recibe.nombre} onChange={setR('nombre')} /></Campo>
                <div className="grid grid-cols-2 gap-2">
                    <Campo label="DNI"><input className={INPUT} inputMode="numeric" value={recibe.dni} onChange={setR('dni')} /></Campo>
                    <Campo label="Cargo"><input className={INPUT} value={recibe.cargo} onChange={setR('cargo')} placeholder="Ej. Administración" /></Campo>
                </div>
                <FirmaPad label="Firma de quien recibe" value={firmaRecibe} onChange={setFirmaRecibe} height={140} />
                <FirmaPad label="Firma de quien entrega (opcional)" value={firmaEntrega} onChange={setFirmaEntrega} height={100} />
                <Campo label="Observaciones (opcional)">
                    <textarea className={`${INPUT} h-20 py-2.5 resize-none`} value={observaciones} onChange={(ev) => setObservaciones(ev.target.value)} />
                </Campo>
            </div>
        </ModalShell>
    );
}
